import * as firebase from "firebase/app";
import { FastAverageColor } from 'fast-average-color';
import ImageCompressor from 'image-compressor.js';

interface Options {
  path: string;
  file: File | Blob;
  token?: string;
  onStart?: (snapshot: firebase.storage.UploadTaskSnapshot) => void;
  onError?: (error: Error) => void;
  onComplete?: (storageRef: firebase.storage.UploadTask) => void;
}

export const uploadImage = (options: Options) => {
  const storage = firebase
    .storage()
    .ref()
    .child(options.path)
    .put(options.file);

  storage.on("state_changed", options.onStart, options.onError, () =>
    options.onComplete(storage)
  );
};

export const uploadImageAsync = async (options: Options) => {
  return await firebase
    .storage()
    .ref()
    .child(options.path)
    .put(options.file);
};

export const uploadFileByBase64 = async ({
  base64,
  path,
  onStart,
  onError
}: {
  base64: string;
  path: string;
  onStart?: Function;
  onError?: Function;
}): Promise<string | null> => {
  try {
    const storageRef = firebase.storage().ref().child(path);

    if (onStart) onStart();

    const snapshot = await storageRef.putString(base64, 'data_url');
    const downloadUrl = await snapshot.ref.getDownloadURL();

    return downloadUrl;
  } catch (error) {
    if (onError) onError(error);
    return null;
  }
};

export const deleteFileByPath = async (path: string) => {
  return await firebase
    .storage()
    .ref()
    .child(path)
    .delete();
}

export const deleteFolderByPath = async (folderPath: string) => {
  try {
    const folderRef = firebase.storage().ref().child(folderPath);

    const listResult = await folderRef.listAll();

    const deletePromises = listResult.items.map(itemRef => itemRef.delete());

    await Promise.all(deletePromises);

    console.log(`Deleted all files from folder: ${folderPath}`);
  } catch (error) {
    console.error(`Error deleting files from folder ${folderPath}:`, error);
  }
};

export const deleteFileByUrl = async (url: string) => {
  try {
    if(!url.includes('firebasestorage.googleapis.com')) return

    const fileRef = firebase.storage().refFromURL(url)

    return await fileRef.delete()
  } catch (error) {
    console.log(error)
    return
  }
}

export const deleteFile = async (pathToFile, fileName) => {
  const ref = firebase.storage().ref(pathToFile)
  const childRef = ref.child(fileName)
  childRef.delete()
}

export const deleteFolder = async(path) => {
  const ref = firebase.storage().ref(path)
  ref.listAll()
    .then(dir => {
      dir.items.forEach(fileRef => deleteFile(ref.fullPath, fileRef.name))
      dir.prefixes.forEach(folderRef => deleteFolder(folderRef.fullPath))
    })
    .catch(error => console.log(error));
}

//add opacity to hex color
export const addAlpha = (color, opacity) => {
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

export const getDominantColor = async(imgUrl) => {
    try {
      //this is not working on localhost
      const fac = new FastAverageColor()
      const res = await fac.getColorAsync(imgUrl, {
        ignoredColor: [255, 255, 255, 255] // white
      })
      return addAlpha(res.hex, 0.07)
    } catch (error) {
      return '#FFFFFF'
    }
}

export const optimizePhotos = async(file, type) => {
  const photoSize = file.size / 1024;
  const minimumSize = 700;

  if(photoSize > minimumSize) {
    const settings = {
      quality: 0.7,
      maxWidth: 1200,
      maxHeight: 1200,
      mimeType: 'image/jpeg'
    };

    if (type === 'content-image') {
      settings.quality = 0.6;
      settings.maxWidth = 700;
      settings.maxHeight = 700;
    }

    try {
      return new Promise((resolve, reject) => {
        new ImageCompressor(file, {
          ...settings,
          success: resolve,
          error: reject,
        });
      });
    } catch (error) {
      console.log(error)
    }
  }

  return file;
}

export const getBase64SizeInMB = (base64: string) => {
  if (!base64.includes(',')) return 0;

  const base64Str = base64.split(',')[1]; // Separate the data after "data:*/*;base64,"
  const sizeInBytes = (base64Str.length * 3) / 4;
  return sizeInBytes / (1024 * 1024); // Mb
}